import { Box, useToast } from "@chakra-ui/react";
import { BaseCategory } from "@the-volte/svc-core-sdk/lib/fragments/gqlTypes/BaseCategory";
import { CategoryDetails } from "@the-volte/svc-core-sdk/lib/fragments/gqlTypes/CategoryDetails";
import { NextPage } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { AiOutlineClose, AiTwotoneInfoCircle } from "react-icons/ai";

import { siteUrl, ssrMode } from "@app/constants";
import { useTikTokPixelContext } from "@app/providers/TikTokPixelProvider";
import { MetaWrapper, NotFound } from "@components/legacy";
import { ProductListPage } from "@components/pages/ProductList/ProductListPage";
import { Attribute } from "@graphql/gqlTypes/Attribute";
import { useAttributesFiltering } from "@hooks/useAttributesFiltering";
import { useDeclinedOrderDiscountVoucher } from "@hooks/useDeclinedOrderDiscountVoucher";
import useProductFiltering from "@hooks/useProductFiltering";
import { useProductLoading } from "@hooks/useProductLoading";

interface CategoryData {
  article: any;
  details: CategoryDetails;
  ancestors: BaseCategory[];
  attributes: Attribute[];
  products: any; // SSR Products Query Result
  id?: string;
}
export type CategoryViewProps = {
  data?: ({ id: string } & CategoryData) | undefined | null;
  isSearchPage?: boolean;
  params: { slug: string; shouldRevalidate?: boolean };
};

export const CategoryView: NextPage<CategoryViewProps> = ({
  data: category,
  isSearchPage,
}) => {
  const attributes = useAttributesFiltering({ categoryId: category?.id });
  const toast = useToast();
  const router = useRouter();

  const defaultSortMetadata = category?.article?.metadata?.find(
    (x) => x.key === "default-sort"
  );

  const extractedArray = defaultSortMetadata?.value.match(/'([^']+)'/);

  const defaultSort = extractedArray ? extractedArray[1] : "best-match";

  const { declinedVoucherCodeParam, voucherValid } =
    useDeclinedOrderDiscountVoucher();

  useEffect(() => {
    if (declinedVoucherCodeParam && voucherValid) {
      toast({
        duration: 3000,
        position: "top-right",
        render: () => (
          <Box
            alignItems="center"
            backgroundColor="var(--chakra-colors-tertiary)"
            boxShadow="dark-lg"
            color="white"
            display="flex"
            flexDir="row"
            gap={2}
            padding={3}
          >
            <AiTwotoneInfoCircle />
            Your discount will be applied at Checkout.
            <AiOutlineClose cursor="pointer" onClick={() => toast.closeAll()} />
          </Box>
        ),
      });
    }
  }, [voucherValid]);

  if (!category && !isSearchPage) {
    return <NotFound />;
  }

  const { track } = useTikTokPixelContext();

  useEffect(() => {
    const handleRouteChange = () => {
      const { pathname, query } = router;
      const searchTerm = query?.keyword;

      if (pathname.includes("search") && searchTerm) {
        track("Search", {
          query: Array.isArray(searchTerm) ? searchTerm.join(", ") : searchTerm,
        });
      }
    };

    router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router, track]);

  const {
    clientFiltered,
    filters,
    pricesFilter,
    locationFilter,
    handleClearFilters,
    handleFiltersChange,
    handlePricesFilter,
    handleLocationFilter,
    handleDeliveryFilter,
    deliveryFilter,
    hasDeliveryFilter,
    handleOrderChange,
    keyword,
    setKeyword,
  } = useProductFiltering();

  const {
    products,
    loading,
    pageInfo,
    numberOfProducts,
    handleLoadMore,
    loadMoreError,
  } = useProductLoading({
    ssrData: category?.products?.products,
    ids: isSearchPage ? {} : { categoryId: category?.id },
    filters,
    hasDeliveryFilter,
    clientFiltered,
  });

  const [activeSortOption, setActiveSortOption] = useState<string | null>(
    () => defaultSort
  );

  const mapSortToBackend = (sort: string | null): string | null => {
    switch (sort) {
      case "price-lowest":
        return "price";
      case "price-highest":
        return "-price";
      case "oldest":
        return "created_at";
      case "newest":
        return "-created_at";
      case "best-match":
        return null; // Backend expects `null` for best match
      default:
        return sort; // Fallback in case of unexpected values
    }
  };

  filters.sortBy = mapSortToBackend(activeSortOption) ?? filters.sortBy;

  const onSortChange = (newSort: string | null) => {
    setActiveSortOption(newSort); // Update user-facing state
    handleOrderChange({
      value: newSort,
    });
  };

  const canonicalRef = category && (
    <Head>
      <link rel="canonical" href={`${siteUrl}${router.asPath}`} />
    </Head>
  );

  const title = isSearchPage
    ? keyword
      ? `Results for "${keyword}"`
      : "All Items for Rent"
    : category?.details?.name;

  const description = isSearchPage
    ? `Search results: ${keyword || ""}`
    : category?.details?.seoDescription;

  const seoTitle = isSearchPage
    ? `Search results: ${keyword || ""}`
    : category?.details?.seoTitle;
  return (
    <MetaWrapper
      meta={{
        description: description || "",
        title: seoTitle || "",
        type: isSearchPage ? "search" : "product.category",
        url: ssrMode ? global.location.href : window.location.href,
        image:
          category?.details?.backgroundImage?.url ||
          (products?.length && products[0].thumbnail2x?.url),
      }}
    >
      {canonicalRef}
      <ProductListPage
        title={title || ""}
        seoDescription={category?.details?.seoDescription || ""}
        textContent={category?.details?.description}
        attributes={attributes}
        clearFilters={handleClearFilters}
        products={products}
        displayLoader={!products.length && loading}
        hasNextPage={!!pageInfo?.hasNextPage}
        numberOfProducts={numberOfProducts}
        activeSortOption={filters.sortBy}
        filters={filters}
        onAttributeFiltersChange={handleFiltersChange}
        onLoadMore={handleLoadMore}
        loadMoreError={loadMoreError}
        activeFilters={Object.keys(filters?.attributes || {}).length}
        // onOrder={handleOrderChange}
        onOrder={(option) => onSortChange(option?.value || null)}
        pricesFilter={pricesFilter}
        setPricesFilter={handlePricesFilter}
        locationFilter={locationFilter}
        setLocationFilter={handleLocationFilter}
        deliveryFilter={deliveryFilter}
        setDeliveryFilter={handleDeliveryFilter}
        keyword={keyword}
        setKeyword={setKeyword}
        isFetching={loading}
      />
    </MetaWrapper>
  );
};
